import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AiJobGenerator from '@/components/admin/AiJobGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    sector: '',
    employment_type: 'Full-time',
    experience: '',
    description: '',
    responsibilities: [''],
    requirements: [''],
    status: 'active'
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, formData, config);
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/admin/jobs`, formData, config);
        toast.success('Job created successfully');
      }
      setIsModalOpen(false);
      resetForm();
      fetchJobs();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      location: job.location,
      sector: job.sector,
      employment_type: job.employment_type,
      experience: job.experience,
      description: job.description,
      responsibilities: job.responsibilities,
      requirements: job.requirements,
      status: job.status
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingJob(null);
    setFormData({
      title: '',
      location: '',
      sector: '',
      employment_type: 'Full-time',
      experience: '',
      description: '',
      responsibilities: [''],
      requirements: [''],
      status: 'active'
    });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const updateArrayField = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <div data-testid="admin-jobs-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-primary">Manage Jobs</h1>
        <Button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          data-testid="add-job-button"
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2" size={20} /> Add Job
        </Button>
      </div>

      <div className="bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase tracking-wider">Sector</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {jobs.map((job) => (
                <tr key={job.id} data-testid={`job-row-${job.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-primary">{job.title}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{job.location}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{job.sector}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-semibold ${job.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(job)}
                      data-testid={`edit-job-${job.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      data-testid={`delete-job-${job.id}`}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="job-modal">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Add New Job'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location *</label>
                <input
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Sector *</label>
                <select
                  required
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                >
                  <option value="">Select Sector</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Automation">Automation</option>
                  <option value="Fashion and Apparel">Fashion and Apparel</option>
                  <option value="Energy">Energy</option>
                  <option value="Emerging Technology">Emerging Technology</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Employment Type *</label>
                <select
                  required
                  value={formData.employment_type}
                  onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Experience *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., 10+ years"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status *</label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <AiJobGenerator
              formData={formData}
              onGenerated={(gen) => setFormData({
                ...formData,
                description: gen.description || formData.description,
                responsibilities: gen.responsibilities?.length ? gen.responsibilities : formData.responsibilities,
                requirements: gen.requirements?.length ? gen.requirements : formData.requirements,
              })}
            />

            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <textarea
                required
                rows={4}
                data-testid="job-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Responsibilities *</label>
              {formData.responsibilities.map((resp, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    required
                    value={resp}
                    onChange={(e) => updateArrayField('responsibilities', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                  />
                  {formData.responsibilities.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayField('responsibilities', index)}
                      className="p-2 text-red-600 hover:bg-red-50"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('responsibilities')}
                className="text-sm text-primary hover:underline"
              >
                + Add Responsibility
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Requirements *</label>
              {formData.requirements.map((req, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    required
                    value={req}
                    onChange={(e) => updateArrayField('requirements', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayField('requirements', index)}
                      className="p-2 text-red-600 hover:bg-red-50"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('requirements')}
                className="text-sm text-primary hover:underline"
              >
                + Add Requirement
              </button>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-job-button">
                {editingJob ? 'Update Job' : 'Create Job'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                data-testid="cancel-job-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobs;
